import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageCircle } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);

export function CTASection() {
  const { items, setDrawerOpen } = useCart();
  const total = items.reduce((s, i) => s + (i.price || 0), 0);

  return (
    <section data-testid="cta-section" className="py-24 md:py-32 px-6 md:px-12 bg-surface border-t border-white/10">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-4xl md:text-5xl text-white mb-8">Ready to Confirm?</h2>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-6 mb-12 text-left">
            {[
              { step: '1', title: 'Review Your Total Estimate', desc: 'Browse our selection and build your perfect event package.' },
              { step: '2', title: 'Submit for Approval', desc: 'Submit for approval & availability check with our team.' },
              { step: '3', title: 'Finalize Your Booking', desc: 'Finalize your booking with 50% deposit once approved (full amount due if booking is within 7 days).' },
            ].map((s) => (
              <div key={s.step} className="p-6 border border-white/10 bg-background/30">
                <div className="w-8 h-8 flex items-center justify-center border border-gold text-gold text-sm font-semibold mb-4">
                  {s.step}
                </div>
                <h3 className="text-white text-sm font-medium mb-2">{s.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Estimate */}
          {items.length > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 border border-gold/30 bg-gold/5 mb-8"
            >
              <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Your Current Estimate</p>
              <p className="font-display text-4xl text-gold">{fmt(total)}</p>
              <p className="text-white/40 text-xs mt-1">{items.length} item{items.length !== 1 ? 's' : ''} selected</p>
              <button
                onClick={() => setDrawerOpen(true)}
                className="mt-4 px-8 py-3 bg-gold text-background text-sm font-semibold uppercase tracking-wide hover:bg-gold-light transition-colors"
                data-testid="cta-view-estimate"
              >
                View & Submit Estimate
              </button>
            </motion.div>
          )}

          {/* Contact */}
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            <a href="tel:+17868915777" className="p-5 border border-white/10 bg-background/30 hover:border-gold/30 transition-colors group" data-testid="cta-call-us">
              <Phone className="w-5 h-5 text-gold mb-3" />
              <p className="text-white text-sm font-medium group-hover:text-gold transition-colors">Call Us</p>
              <p className="text-white/40 text-xs">(786) 891-5777</p>
            </a>
            <a href="sms:+17868915777" className="p-5 border border-white/10 bg-background/30 hover:border-gold/30 transition-colors group" data-testid="cta-text-us">
              <MessageCircle className="w-5 h-5 text-gold mb-3" />
              <p className="text-white text-sm font-medium group-hover:text-gold transition-colors">Text Us</p>
              <p className="text-white/40 text-xs">(786) 891-5777</p>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
